// src/pages/VehiclesTax.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HeaderLogged from '../components/HeaderLogged';
import Footer from '../components/Footer';
import VehicleModal from '../components/VehicleModal';
import EditVehicleModal from '../components/EditVehicleModal';
import { useWizardFormId, withForm, formBelongsToCompany } from '../utils/wizardForm';

// Таблица категорий
const WEIGHT_CATEGORIES = [
  { letter: 'A', range: '55,000', tax: 100 },
  { letter: 'B', range: '55,001 - 56,000', tax: 122 },
  { letter: 'C', range: '56,001 - 57,000', tax: 144 },
  { letter: 'D', range: '57,001 - 58,000', tax: 166 },
  { letter: 'E', range: '58,001 - 59,000', tax: 188 },
  { letter: 'F', range: '59,001 - 60,000', tax: 210 },
  { letter: 'G', range: '60,001 - 61,000', tax: 232 },
  { letter: 'H', range: '61,001 - 62,000', tax: 254 },
  { letter: 'I', range: '62,001 - 63,000', tax: 276 },
  { letter: 'J', range: '63,001 - 64,000', tax: 298 },
  { letter: 'K', range: '64,001 - 65,000', tax: 320 },
  { letter: 'L', range: '65,001 - 66,000', tax: 342 },
  { letter: 'M', range: '66,001 - 67,000', tax: 364 },
  { letter: 'N', range: '67,001 - 68,000', tax: 386 },
  { letter: 'O', range: '68,001 - 69,000', tax: 408 },
  { letter: 'P', range: '69,001 - 70,000', tax: 430 },
  { letter: 'Q', range: '70,001 - 71,000', tax: 452 },
  { letter: 'R', range: '71,001 - 72,000', tax: 474 },
  { letter: 'S', range: '72,001 - 73,000', tax: 496 },
  { letter: 'T', range: '73,001 - 74,000', tax: 518 },
  { letter: 'U', range: '74,001 - 75,000', tax: 540 },
  { letter: 'V', range: 'over 75,000', tax: 550 },
  // Tax-suspended vehicle: expected to run ≤5,000 miles (7,500 agricultural)
  // during the period — reported on Schedule 1, no tax due.
  { letter: 'W', range: 'Tax Suspended', tax: 0 },
];

// Для расчёта проре́йта
const MONTH_MAP = {
  July: 0, August: 1, September: 2, October: 3, November: 4, December: 5,
  January: 6, February: 7, March: 8, April: 9, May: 10, June: 11,
};
function prorateTax(annualTax, startMonthName) {
  const idx = MONTH_MAP[startMonthName];
  if (idx == null) return annualTax;
  const monthsLeft = 12 - idx;
  return Math.round((annualTax * monthsLeft / 12) * 100) / 100;
}

export default function VehiclesTax() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Номер формы — из адреса этой вкладки (см. utils/wizardForm.js)
  const formId = useWizardFormId();

  // загрузка
  const [loading, setLoading]         = useState(true);
  const [company, setCompany]         = useState(null);
  const [vehicles, setVehicles]       = useState([]);
  const [firstUsedMonth, setFirstUsedMonth] = useState('');
  const [knownVins, setKnownVins]     = useState([]);

  // ошибка, если не добавили ни одной машины
  const [continueError, setContinueError] = useState('');

  // --- Add Vehicle Modal ---
  const [isAddOpen, setIsAddOpen]   = useState(false);
  const [vin, setVin]               = useState('');
  const [category, setCategory]     = useState('');
  const [logging, setLogging]       = useState('');
  const [taxAmount, setTaxAmount]   = useState(0);
  const [isAgricultural, setIsAgricultural] = useState(false); // W only: 7,500-mile limit

  // --- Edit Vehicle Modal ---
  const [isEditOpen, setIsEditOpen]       = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [editVin, setEditVin]             = useState('');
  const [editCategory, setEditCategory]   = useState('');
  const [editRange, setEditRange]         = useState('');
  const [editTaxAmount, setEditTaxAmount] = useState(0);
  const [editLogging, setEditLogging]     = useState('');
  const [editIsAgricultural, setEditIsAgricultural] = useState(false);

  // Шаги прогресса
  const steps = [
    { id: 1, label: 'Tax & Form' },
    { id: 2, label: 'Vehicles Tax' },
    { id: 3, label: 'IRS Payment Options' },
    { id: 4, label: 'Form Summary' },
    { id: 5, label: 'Payment and IRS Submission' },
    { id: 6, label: 'IRS Confirmation' },
  ];
  const currentStep = 2;

  // Загрузка компании + списка машин
  useEffect(() => {
    async function fetchAll() {
      if (!formId) return; // useWizardFormId уже уводит на страницу компании
      try {
        const token = localStorage.getItem('token');
        // company
        const rc = await fetch(`/api/companies/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCompany(await rc.json());
        // form2290 → firstUsedMonth + vehicle IDs
        const rf = await fetch(`/api/form2290/${formId}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const form = await rf.json();
        // Форма обязана принадлежать компании из адреса — иначе машины уедут
        // в чужую декларацию. Ничего не показываем и уходим к компании.
        if (!formBelongsToCompany(form, id)) {
          navigate(`/company/${id}`, { replace: true });
          return;
        }
        setFirstUsedMonth(form.first_used_month);
        // детали машин
        const vs = await Promise.all(
          (form.vehicles || []).map(async vid => {
            const rv = await fetch(`/api/vehicles/${vid}/`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            return rv.json();
          })
        );
        setVehicles(vs);

        // known VINs across this company's previous forms
        try {
          const kv = await fetch(`/api/companies/${id}/known-vins/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (kv.ok) {
            setKnownVins(await kv.json());
          }
        } catch (_) {
          // не критично — просто не покажем подсказки
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
  }, [id, formId, navigate]);

  // При изменении списка машин — сброс ошибки
  useEffect(() => {
    if (vehicles.length > 0) setContinueError('');
  }, [vehicles]);

  // Открыть Add-модалку
  const openAdd = () => setIsAddOpen(true);
  const closeAdd = () => {
    setIsAddOpen(false);
    setVin(''); setCategory(''); setLogging(''); setTaxAmount(0);
    setIsAgricultural(false);
  };

  // При выборе категории в Add
  const handleAddCategoryChange = e => {
    const letter = e.target.value;
    setCategory(letter);
    const cat = WEIGHT_CATEGORIES.find(c => c.letter === letter);
    if (cat) {
      setTaxAmount(
        firstUsedMonth
          ? prorateTax(cat.tax, firstUsedMonth)
          : cat.tax
      );
    } else {
      setTaxAmount(0);
    }
  };

  // Выбор VIN из ранее использованных (предзаполняет VIN + категорию + налог)
  const handlePickKnownVin = (vehicleInfo) => {
    if (!vehicleInfo) return;
    setVin(vehicleInfo.vin || '');
    const letter = vehicleInfo.gross_vehicle_label || '';
    setCategory(letter);
    setIsAgricultural(!!vehicleInfo.is_agricultural);
    const cat = WEIGHT_CATEGORIES.find(c => c.letter === letter);
    if (cat) {
      setTaxAmount(
        firstUsedMonth ? prorateTax(cat.tax, firstUsedMonth) : cat.tax
      );
    }
  };

  // Сохранить новую машину
  const handleAddSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const cat = WEIGHT_CATEGORIES.find(c => c.letter === category);
      if (!cat) throw new Error('Select weight category');

      // 1) создаём vehicle
      const rv = await fetch(`/api/vehicles/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          vin,
          gross_vehicle_weight: cat.range,
          gross_vehicle_label: cat.letter,
          taxable_weight: taxAmount,
          description: logging,
          is_agricultural: cat.letter === 'W' ? isAgricultural : false,
          company: id,
        }),
      });
      if (!rv.ok) throw new Error('Error creating vehicle');
      const newVeh = await rv.json();

      // 2) связываем с form2290 — той, что открыта в этой вкладке
      const link = await fetch(
        `/api/form2290/${formId}/add_vehicle/${newVeh.id}/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!link.ok) throw new Error('Error linking vehicle');

      // 3) локально добавляем
      setVehicles(vs => [...vs, newVeh]);
      closeAdd();
    } catch (e) {
      console.error(e);
    }
  };

  // Открыть Edit-модалку
  const openEdit = v => {
    setEditingVehicle(v);
    setEditVin(v.vin);
    setEditLogging(v.description || '');
    setEditIsAgricultural(!!v.is_agricultural);
    // найти категорию по букве
    const cat = WEIGHT_CATEGORIES.find(c => c.letter === v.gross_vehicle_label);
    if (cat) {
      setEditCategory(cat.letter);
      setEditRange(cat.range);
      setEditTaxAmount(
        firstUsedMonth
          ? prorateTax(cat.tax, firstUsedMonth)
          : cat.tax
      );
    }
    setIsEditOpen(true);
  };

  // При смене категории в Edit
  const handleEditCategoryChange = e => {
    const letter = e.target.value;
    setEditCategory(letter);
    const cat = WEIGHT_CATEGORIES.find(c => c.letter === letter);
    if (cat) {
      setEditRange(cat.range);
      setEditTaxAmount(
        firstUsedMonth
          ? prorateTax(cat.tax, firstUsedMonth)
          : cat.tax
      );
    } else {
      setEditRange('');
      setEditTaxAmount(0);
    }
  };

  // Сохранить редактирование
  const handleEditSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `/api/vehicles/${editingVehicle.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            vin: editVin,
            gross_vehicle_weight: editRange,
            gross_vehicle_label: editCategory,
            taxable_weight: editTaxAmount,
            description: editLogging,
            is_agricultural: editCategory === 'W' ? editIsAgricultural : false,
          }),
        }
      );
      if (!res.ok) throw new Error('Error updating vehicle');
      const updated = await res.json();
      setVehicles(vs => vs.map(v => v.id === updated.id ? updated : v));
      setIsEditOpen(false);
    } catch (e) {
      console.error(e);
    }
  };

  // Удалить машину
  const handleDelete = async vid => {
    if (!window.confirm('Are you sure?')) return;
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(
        `/api/form2290/${formId}/delete_vehicle/${vid}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!r.ok) throw new Error('Error deleting vehicle');
      setVehicles(vs => vs.filter(v => v.id !== vid));
    } catch (e) {
      console.error(e);
    }
  };

  // Continue Filing
  const handleContinue = async () => {
    if (!vehicles.length) {
      setContinueError('Please add a vehicle first');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/form2290/${formId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ step: 2 }),
      });
      navigate(withForm(`/company/${id}/file-form/irs-payment-options`, formId));
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#E4F2FF]">
        <p className="text-gray-700 text-lg">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <HeaderLogged />

      {/* Progress bar */}
      <div className="bg-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap justify-center gap-3">
          {steps.map(step => {
            const isCurrent = step.id === currentStep;
            const isNext    = step.id === currentStep + 1;
            const isCompleted = step.id < currentStep;

            const baseClasses = 'text-sm font-medium px-3 py-1 border';
            const visibleClasses = isCurrent || isNext || isCompleted
              ? 'block'
              : 'hidden sm:block';

            const colorClasses = isCurrent
              ? 'bg-[#0F2C5E] text-white border-[#0F2C5E]'
              : isCompleted
                ? 'bg-green-100 text-green-700 border-green-200'
                : isNext
                  ? 'bg-[#E4E8F0] text-[#0F2C5E] border-[#E4E8F0]'
                  : 'bg-gray-100 text-gray-600 border-gray-200';

            return (
              <div
                key={step.id}
                className={`${baseClasses} ${visibleClasses} ${colorClasses}`}
              >
                {step.label}
              </div>
            );
          })}
        </div>
      </div>
      {/* Main */}
      <main className="flex-1 bg-[#E4F2C5E] py-6 px-4">
        <div className="max-w-6xl mx-auto bg-white border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-[#0F2C5E]">Vehicles Tax</h1>
            <button
              onClick={openAdd}
              className="bg-[#0F2C5E] text-white px-5 py-2 hover:bg-[#0F2C5E]/80 transition"
            >
              Add Vehicle
            </button>
          </div>
           <div className="p-6 space-y-6">
            {/* Горизонтальный скролл для таблицы */}
            <div className="overflow-x-auto">
              <table className="w-full table-auto whitespace-nowrap">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left">VIN</th>
                    <th className="px-4 py-2 text-left">Category</th>
                    <th className="px-4 py-2 text-left">Gross Weight</th>
                    <th className="px-4 py-2 text-left hidden sm:table-cell">
                      Logging
                    </th>
                    <th className="px-4 py-2 text-left hidden md:table-cell">
                      Tax
                    </th>
                    <th className="px-4 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.length > 0 ? (
                    vehicles.map(v => (
                      <tr key={v.id} className="border-b border-gray-100">
                        <td className="px-4 py-2">{v.vin}</td>
                        <td className="px-4 py-2">{v.gross_vehicle_label}</td>
                        <td className="px-4 py-2">{v.gross_vehicle_weight}</td>
                        <td className="px-4 py-2 hidden sm:table-cell">
                          {v.description}
                        </td>
                        <td className="px-4 py-2 hidden md:table-cell">
                          ${v.taxable_weight}
                        </td>
                        <td className="px-4 py-2 space-x-2">
                        <button
                          onClick={() => openEdit(v)}
                          className="text-blue-600 hover:underline text-xs"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(v.id)}
                          className="text-red-600 hover:underline text-xs"
                        >
                          Delete
                        </button>
                      </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        No vehicles added yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {continueError && (
              <p className="text-red-600 text-sm">{continueError}</p>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleContinue}
                disabled={!vehicles.length}
                className={`px-5 py-2 text-white transition ${
                  vehicles.length
                    ? 'bg-[#0F2C5E] hover:bg-[#0F2C5E]/80'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                Continue Filing
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      <VehicleModal
        isOpen={isAddOpen}
        onClose={closeAdd}
        onSave={handleAddSave}
        vin={vin}
        setVin={setVin}
        category={category}
        handleCategoryChange={handleAddCategoryChange}
        logging={logging}
        setLogging={setLogging}
        taxAmount={taxAmount}
        isAgricultural={isAgricultural}
        setIsAgricultural={setIsAgricultural}
        weightCategories={WEIGHT_CATEGORIES}
        knownVins={knownVins.filter(kv => !vehicles.some(v => v.vin === kv.vin))}
        onPickKnownVin={handlePickKnownVin}
      />

      <EditVehicleModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        vin={editVin}
        onVinChange={setEditVin}
        category={editCategory}
        onCategoryChange={handleEditCategoryChange}
        range={editRange}
        taxAmount={editTaxAmount}
        logging={editLogging}
        onLoggingChange={setEditLogging}
        isAgricultural={editIsAgricultural}
        onAgriculturalChange={setEditIsAgricultural}
        weightCategories={WEIGHT_CATEGORIES}
        onSave={handleEditSave}
      />

      <Footer />
    </div>
  );
}
